/* «Wyceń zestaw» — the popup's own interface touches. Added 2026-09-22 on the owner's notes.
   It changes nothing about the data: the original <select> keeps its id, its name and its value, and every change
   is dispatched on it, so the step logic and the Shopify contact form behave exactly as before.

   1. The year is typed instead of hunted through a native dropdown of 48 options (a combobox after WAI-ARIA).
   2. A swatch's colour name is shown next to its group title while the pointer is on it, because the theme's own
      label is an 8 px tooltip that paints under the neighbouring swatches.
   3. The mat preview carries its caption on a wide screen too.
   5. (28.09.2026, owner photos 4–5) The chosen «Rodzaj»/«Zestaw» are mirrored into contact[...] fields (the radios never
      reached the sales e-mail); heel pads are picked option by option (colour swatches, size/type, mounting) and the
      chosen variant's full title is kept in the item's hidden contact field. No prices anywhere in the popup (owner,
      28.09 evening: it asks for a quote).
*/
(() => {
  const ROOT_ID = 'popupOverlayApplicationForm';
  const ready = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn());

  const norm = (s) => String(s || '').trim().toLowerCase();

  // ---------- 1. the year, typed ----------
  function buildYearCombo(root) {
    const select = root.querySelector('#rok-produkcji');
    if (!select || select.dataset.afCombo) return;
    select.dataset.afCombo = '1';

    const options = [...select.options].filter((o) => o.value !== '');
    if (!options.length) return;

    const wrap = document.createElement('div');
    wrap.className = 'af-combo';
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'af-combo__input';
    input.id = 'af-year-input';
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', 'af-year-list');
    input.setAttribute('aria-label', 'Rok produkcji');
    input.placeholder = 'np. 2019';
    input.autocomplete = 'off';
    input.inputMode = 'numeric';
    input.enterKeyHint = 'done';

    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'af-combo__clear';
    clear.setAttribute('aria-label', 'Wyczyść');
    clear.hidden = true;
    clear.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>';

    const list = document.createElement('ul');
    list.className = 'af-combo__list';
    list.id = 'af-year-list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Rok produkcji');
    list.hidden = true;

    wrap.append(input, clear, list);
    select.parentElement.insertBefore(wrap, select);
    select.classList.add('af-visually-hidden');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    let active = -1;
    let shown = [];

    const render = (q) => {
      const query = norm(q);
      // the newest years first: someone typing a year is far more likely to own a recent car
      const all = options.slice().reverse();
      shown = query ? all.filter((o) => o.value.includes(query)) : all;
      list.innerHTML = '';
      if (!shown.length) {
        const li = document.createElement('li');
        li.className = 'af-combo__empty';
        li.textContent = 'Nie mamy takiego rocznika na liście.';
        list.append(li);
        return;
      }
      shown.forEach((o, i) => {
        const li = document.createElement('li');
        li.className = 'af-combo__item';
        li.id = `af-year-${o.value}`;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', String(select.value === o.value));
        li.dataset.value = o.value;
        li.textContent = o.value;
        if (i === active) li.classList.add('is-active');
        list.append(li);
      });
    };

    const open = () => { render(input.value); list.hidden = false; input.setAttribute('aria-expanded', 'true'); };
    const close = () => { list.hidden = true; input.setAttribute('aria-expanded', 'false'); active = -1; input.removeAttribute('aria-activedescendant'); };

    const pick = (value) => {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      input.value = value;
      clear.hidden = false;
      close();
    };

    const move = (step) => {
      if (list.hidden) open();
      if (!shown.length) return;
      active = (active + step + shown.length) % shown.length;
      render(input.value);
      const li = list.children[active];
      if (li) { li.scrollIntoView({ block: 'nearest' }); input.setAttribute('aria-activedescendant', li.id); }
    };

    input.addEventListener('focus', open);
    input.addEventListener('input', () => { active = -1; clear.hidden = !input.value; open(); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
      else if (e.key === 'Enter') {
        // always: this input sits inside form#application_form_id, which holds #submit-btn[type=submit], so an
        // Enter that is not cancelled here is an implicit submit of the whole quote from the middle of step 1
        e.preventDefault();
        const value = active > -1 && shown[active] ? shown[active].value : (shown.length === 1 ? shown[0].value : null);
        if (value) pick(value);
      } else if (e.key === 'Escape') { close(); }
    });
    // A mouse press is cancelled so the input keeps focus and the list is still there when the click lands.
    // A touch is left alone: preventDefault on pointerdown cancels the gesture the browser would scroll the
    // 48-year list with, so the year is taken on pointerup instead, and only if the finger did not travel.
    let touch = null;
    list.addEventListener('pointerdown', (e) => {
      const li = e.target.closest('.af-combo__item');
      if (!li) return;
      if (e.pointerType === 'mouse') { e.preventDefault(); pick(li.dataset.value); return; }
      touch = { x: e.clientX, y: e.clientY, value: li.dataset.value };
    });
    list.addEventListener('pointerup', (e) => {
      if (!touch) return;
      const { x, y, value } = touch;
      touch = null;
      if (Math.hypot(e.clientX - x, e.clientY - y) <= 10) pick(value);
    });
    list.addEventListener('pointercancel', () => { touch = null; });
    clear.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      input.value = '';
      clear.hidden = true;
      select.value = '';
      select.dispatchEvent(new Event('change', { bubbles: true }));
      input.focus();
      open();
    });
    // a touch on the list blurs the input, and closing 120 ms later would take the list away under the finger
    input.addEventListener('blur', () => { setTimeout(() => { if (!touch && !wrap.contains(document.activeElement)) close(); }, 120); });
    // isConnected: a section re-render builds a new combo, and the listener of the old one would otherwise
    // keep firing against a detached list for the rest of the page's life
    document.addEventListener('pointerdown', (e) => { if (wrap.isConnected && !wrap.contains(e.target)) close(); });

    if (select.value) { input.value = select.value; clear.hidden = false; }
  }

  // ---------- 2. the hovered colour's name, next to its group title ----------
  function wireSwatchNames(root) {
    for (const field of root.querySelectorAll('.lip__field')) {
      const title = field.querySelector('.lip__title');
      const value = title && title.querySelector('.lip__title_value');
      if (!value || field.dataset.afSwatch) continue;
      const items = field.querySelectorAll('.lip__item');
      if (!items.length) continue;
      field.dataset.afSwatch = '1';
      const show = (e) => {
        const item = e.target.closest('.lip__item');
        if (!item) return;
        const name = item.querySelector('.label__tooltip')?.textContent.trim() || item.querySelector('input')?.dataset.title;
        if (!name) return;
        if (value.dataset.afPicked === undefined) value.dataset.afPicked = value.textContent.trim();
        value.textContent = name;
        value.classList.add('is-preview');
      };
      const restore = () => {
        if (value.dataset.afPicked === undefined) return;
        const picked = field.querySelector('.lip__item input:checked');
        value.textContent = picked?.dataset.title || value.dataset.afPicked;
        value.classList.remove('is-preview');
      };
      field.addEventListener('pointerover', show);
      field.addEventListener('focusin', show);
      field.addEventListener('pointerleave', restore);
      field.addEventListener('focusout', restore);
      field.addEventListener('change', () => { delete value.dataset.afPicked; value.classList.remove('is-preview'); });
      // pointerleave does not always arrive (a pointer that jumps, or a synthetic one), and a stale colour name in
      // the chip would read as the chosen colour — so anything hovered elsewhere restores this field as well
      field.afRestore = restore;
    }
  }

  // ---------- 3. the phone preview: the colour step's own button opens it as a small window ----------
  // It used to be a plain child of the column sitting between step 4 and «Podpiętki», which is where it surfaced.
  // Inline it also stayed invisible: the theme animates that block with opacity and height, and nothing in the
  // popup triggers that animation — so the window sets both itself.
  function previewOnDemand(root) {
    const mob = root.querySelector('.product__config_image--mobile');
    if (!mob || mob.dataset.afMoved) return;
    const step = [...root.querySelectorAll('.product__dropdown_wr')]
      .find((w) => /kszta[łl]t|kolor/i.test(w.querySelector('.product__dropdown_title')?.textContent || ''));
    const inner = step && step.querySelector('.product__dropdown_inner');
    if (!inner) return;
    mob.dataset.afMoved = '1';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'af-preview__toggle';
    btn.setAttribute('aria-expanded', 'false');
    const eye = document.createElement('span');
    eye.className = 'af-preview__eye';
    eye.setAttribute('aria-hidden', 'true');
    btn.append(eye, document.createTextNode('Zobacz podgląd dywanika'));

    const box = document.createElement('div');
    box.className = 'af-preview';
    box.append(btn);

    // the window itself: a small sheet over the card, with its own close control
    const win = document.createElement('div');
    win.className = 'af-pv';
    win.hidden = true;
    const card = document.createElement('div');
    card.className = 'af-pv__card';
    const head = document.createElement('div');
    head.className = 'af-pv__head';
    const title = document.createElement('p');
    title.className = 'af-pv__title';
    title.textContent = 'Podgląd dywanika';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'af-pv__close';
    close.setAttribute('aria-label', 'Zamknij podgląd');
    close.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>';
    head.append(title, close);
    const body = document.createElement('div');
    body.className = 'af-pv__body';
    body.append(mob);
    const note = document.createElement('p');
    note.className = 'af-pv__note';
    note.textContent = '*Podgląd poglądowy — nie jest zdjęciem gotowego produktu.';
    card.append(head, body, note);
    win.append(card);

    const next = inner.querySelector('.product__dropdown_button');
    if (next) inner.insertBefore(box, next); else inner.append(box);
    // The window must stay INSIDE .application_form__content: product-config.js refreshes the preview with
    // `container.querySelectorAll('.product__config_image--mobile_image')`, where container is that element
    // (it even tests `container.classList.contains('application_form__content')`). Appending the window to
    // .popup took the image out of that container and the preview stopped following the chosen colours.
    // The window is position:fixed, so where it sits in the DOM does not change where it is drawn.
    (root.querySelector('.application_form__content') || root.querySelector('.popup'))?.append(win);

    const setOpen = (open) => {
      win.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      btn.classList.toggle('is-open', open);
      if (open) close.focus(); else btn.focus();
    };
    btn.addEventListener('click', () => setOpen(true));
    close.addEventListener('click', () => setOpen(false));
    win.addEventListener('click', (e) => { if (e.target === win) setOpen(false); });
    root.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !win.hidden) { e.stopPropagation(); setOpen(false); } });
  }

  // ---------- 5a. the chosen «Rodzaj» / «Zestaw» reach the sales e-mail ----------
  /* The radios are not contact[...] fields, so the e-mail never carried them; two hidden mirrors in the section do.
     No prices: the owner (28.09 evening) — the popup asks for a quote, it shows no prices at all. */
  function wireMirrors(root) {
    if (root.dataset.afMirrors) return;
    const setFs = root.querySelector('fieldset[data-name="Zestaw"]');
    if (!setFs) return;
    root.dataset.afMirrors = '1';
    const sync = () => {
      const mt = root.querySelector('[data-af-mirror="mats_type"]'); if (mt) mt.value = root.querySelector('input[name="mats_type"]:checked')?.value || '3D BEZ RANTÓW';
      const zs = root.querySelector('[data-af-mirror="Zestaw"]'); if (zs) zs.value = root.querySelector('input[name="Zestaw"]:checked')?.value || '';
    };
    root.addEventListener('change', (e) => { if (e.target.matches?.('input[name="mats_type"], input[name="Zestaw"]')) sync(); });
    // the theme re-renders the tile row on some changes; re-apply after it settles
    root.addEventListener('click', (e) => { if (e.target.closest?.('fieldset[data-name="Zestaw"], fieldset[data-name="mats_type"]')) setTimeout(sync, 0); });
    sync();
  }

  // ---------- 5b. heel pads, option by option ----------
  function wireHeelPads(root) {
    root.querySelectorAll('[data-af-heel]').forEach((item) => {
      if (item.dataset.afHeelWired) return;
      item.dataset.afHeelWired = '1';
      let variants = [];
      try { variants = JSON.parse(item.getAttribute('data-variants') || '[]'); } catch (e) { variants = []; }
      const box = item.querySelector('.product__upsell_checkbox');
      const hidden = item.querySelector('[data-af-heel-variant]');
      const groups = [...item.querySelectorAll('[data-af-heel-group]')];
      const pick = () => {
        const chosen = groups.map((g) => ({ i: Number(g.getAttribute('data-option-index')), v: g.querySelector('[data-af-heel-opt][aria-checked="true"]')?.getAttribute('data-value') }));
        const v = variants.find((x) => chosen.every((c) => (x.options || [])[c.i] === c.v)) || variants[0];
        if (!v) return;
        if (hidden) hidden.value = ` ${v.title}`; // same shape as before (leading space) — the sales e-mail reads it unchanged
        item.classList.toggle('af-heel-unavailable', v.available === false);
      };
      item.addEventListener('click', (e) => {
        const opt = e.target.closest?.('[data-af-heel-opt]');
        if (!opt || !item.contains(opt)) return;
        e.preventDefault();
        const group = opt.closest('[data-af-heel-group]');
        group.querySelectorAll('[data-af-heel-opt]').forEach((o) => o.setAttribute('aria-checked', o === opt ? 'true' : 'false'));
        const val = group.querySelector('[data-af-heel-value]'); if (val) val.textContent = opt.getAttribute('data-value');
        pick();
        // choosing an option means the shopper wants this pad: tick it (the section's own handler enables the field)
        if (box && !box.checked) { box.checked = true; box.dispatchEvent(new Event('change', { bubbles: true })); }
      });
      // a ticked pad opens its options (CSS), as the configurator on the product page does
      const open = () => {
        const on = !!(box && box.checked);
        item.classList.toggle('af-heel-on', on);
        // przeglądarka potrafi przywrócić zaznaczenie (wstecz) bez zdarzenia change — pole wariantu musi iść za checkboxem
        if (hidden) hidden.disabled = !on;
      };
      if (box) box.addEventListener('change', open);
      open();
      // arrow keys inside a group, like a radio group
      item.addEventListener('keydown', (e) => {
        const opt = e.target.closest?.('[data-af-heel-opt]'); if (!opt || !['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
        const all = [...opt.closest('[data-af-heel-group]').querySelectorAll('[data-af-heel-opt]')];
        const next = all[(all.indexOf(opt) + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : all.length - 1)) % all.length];
        e.preventDefault(); next.focus(); next.click();
      });
      pick();
    });
  }

  // ---------- 6. the popup's <variant-radios> is not a product form ----------
  /* The Rodzaj/Zestaw tiles sit in a <variant-radios> for the home page's CSS (product-config.css styles that tag).
     On a product page product.js upgrades it to the theme's VariantRadios, whose change handler reads a variant JSON
     the popup never had: «Cannot read properties of null (reading 'textContent')» on every tile click, since the
     popup existed. These three instance methods make that handler a no-op for this one element; the handler itself,
     the product page's own <variant-radios> and product.js stay untouched. */
  function quietVariantRadios(root) {
    root.querySelectorAll('variant-radios').forEach((vr) => {
      if (vr.afQuiet) return;
      vr.afQuiet = true;
      vr.getVariantData = () => [];
      vr.updateVariantStatuses = () => {};
      vr.setUnavailable = () => {};
    });
  }

  // ---------- 7. a label in the popup works on the popup's own field ----------
  /* The popup repeats the page's field ids (Sedan_1, uwagi-do-zamowienia, ContactForm-name…), and <label for> acts on
     the FIRST element with that id in the document — on a page with its own form that is the page's field. On the live
     zamówienie indywidualne tapping «Sedan» ticked the page's radio, the popup's stayed empty and «Dalej» never
     unlocked; on product pages tapping «Imię i nazwisko» focused a hidden field of the page. Renaming the ids would
     touch product-config.js, evm-mat-cfg-persist.js and the CSS that use them; this only redirects the label. */
  function scopeLabels(root) {
    if (root.afLabels) return;
    root.afLabels = true;
    root.addEventListener('click', (e) => {
      const lab = e.target.closest?.('label[for]');
      if (!lab || !root.contains(lab) || e.target.closest('input, select, textarea, button, a') !== null) return;
      const id = lab.getAttribute('for');
      const own = id && root.querySelector(`#${CSS.escape(id)}`);
      if (!own || document.getElementById(id) === own) return; // the id is unique: the browser already does it right
      e.preventDefault();
      if (own.type === 'radio' || own.type === 'checkbox') own.click(); // same events as a real tap: click, input, change
      else own.focus();
    });
  }

  // ---------- 4. the preview's caption on a wide screen ----------
  function addPreviewNote(root) {
    const box = root.querySelector('[data-af-preview]');
    if (!box || box.querySelector('.af-pv-note')) return;
    const note = document.createElement('p');
    note.className = 'af-pv-note';
    note.textContent = '*Podgląd poglądowy — nie jest zdjęciem gotowego produktu.';
    box.append(note);
  }

  ready(() => {
    const root = document.getElementById(ROOT_ID);
    if (!root) return;
    const apply = () => { buildYearCombo(root); wireSwatchNames(root); previewOnDemand(root); addPreviewNote(root); wireMirrors(root); wireHeelPads(root); quietVariantRadios(root); scopeLabels(root); };
    apply();
    root.addEventListener('pointerover', (e) => {
      const inside = e.target.closest?.('.lip__field');
      for (const f of root.querySelectorAll('.lip__field')) if (f !== inside && typeof f.afRestore === 'function') f.afRestore();
    });
    // the later steps are rendered as the reader advances
    new MutationObserver(() => { buildYearCombo(root); wireSwatchNames(root); previewOnDemand(root); wireMirrors(root); wireHeelPads(root); quietVariantRadios(root); }).observe(root, { childList: true, subtree: true });
  });
})();
